import type { HttpClient } from '../core/http.js';
import type { Page, RequestOptions } from '../types/common.js';
import type {
  ListTransactionsParams,
  Transaction,
  TransactionDetail,
} from '../types/transaction.js';

export class TransactionsResource {
  constructor(private readonly http: HttpClient) {}

  /**
   * List transactions across your bank accounts. Scope: `transactions:read`.
   *
   * @remarks
   * `limit` is capped at 50 server-side. `search` matches `referenceCode` and
   * `transferContent`; `startDate`/`endDate` filter on `transactionTime`.
   */
  list(
    params: ListTransactionsParams = {},
    options: RequestOptions = {},
  ): Promise<Page<Transaction>> {
    return this.http.requestList<Transaction>('GET', '/transactions', {
      ...options,
      query: params as Record<string, unknown>,
    });
  }

  /**
   * Iterate every matching transaction, fetching pages as needed.
   *
   * @example
   * for await (const txn of client.transactions.listAll({ type: 'IN' })) {
   *   console.log(txn.referenceCode);
   * }
   */
  async *listAll(
    params: Omit<ListTransactionsParams, 'page'> = {},
    options: RequestOptions = {},
  ): AsyncGenerator<Transaction, void, undefined> {
    let page = 1;
    for (;;) {
      const result = await this.list({ ...params, page }, options);
      for (const txn of result.data) yield txn;

      if (result.data.length === 0) return;
      if (page >= result.meta.totalPages) return;
      page++;
    }
  }

  /** Fetch one transaction. Scope: `transactions:read`. */
  retrieve(
    id: string,
    options: RequestOptions = {},
  ): Promise<TransactionDetail> {
    return this.http.request<TransactionDetail>(
      'GET',
      `/transactions/${encodeURIComponent(id)}`,
      options,
    );
  }
}
